import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Compass, MapPin } from 'lucide-react';
import Footer from '../components/Footer';
import { ACCOMMODATIONS, TASTING_STOPS } from '../lib/data';
import { getRegionsForState, getStateHubBySlug } from '../lib/trailHubs';
import { bookingSearchUrl } from '../utils/booking';
import { setPageSeo } from '../utils/seo';
import NotFoundPage from './NotFoundPage';

export default function StateHubPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug;
  const hub = slug ? getStateHubBySlug(slug) : undefined;

  useEffect(() => {
    if (hub) {
      setPageSeo({
        title: 'Oregon Truffle Guide | Published Route Zones',
        description:
          'Explore the current Oregon truffle guide zones, including Portland, Willamette Valley, Eugene, and Corvallis, with featured stops, places to stay, and seasonal trip ideas.',
        path: `/${hub.slug}`,
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Oregon Truffle Guide',
          url: `https://trufflehuntingoregon.com/${hub.slug}`,
          description:
            'An Oregon travel guide connecting the current published truffle zones for weekend planning.',
        },
      });
    }
  }, [hub]);

  if (!hub) {
    return <NotFoundPage />;
  }

  const regions = getRegionsForState(hub.state);

  return (
    <div className="min-h-screen bg-bg">
      <header className="border-b border-border bg-white/85 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent text-white flex items-center justify-center">
              <Compass size={18} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">Truffle Hunting Oregon</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-light">Oregon guide</div>
            </div>
          </Link>
          <Link to="/" className="btn-minimal inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to route
          </Link>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-12">
        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          <div className="space-y-6">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-border rounded-full text-[10px] font-bold uppercase tracking-widest text-ink">
              <MapPin size={12} className="text-accent" />
              {hub.eyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-[1.02]">{hub.title}</h1>
            <p className="text-lg md:text-xl text-ink-light leading-relaxed max-w-4xl">{hub.subtitle}</p>
            <div className="flex flex-wrap gap-2">
              {hub.bestFor.map((item) => (
                <span key={item} className="px-3 py-1 bg-sidebar text-ink text-[10px] font-bold uppercase tracking-widest rounded-full">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <article className="rounded-[32px] border border-border bg-[linear-gradient(160deg,rgba(41,67,42,0.92),rgba(106,81,49,0.86))] min-h-[320px] p-8 text-white flex flex-col justify-end">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80">Weekend shape</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-serif tracking-tight max-w-lg">
              Start with the Oregon zones that already have usable public structure.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed max-w-lg">
              Portland, Willamette Valley, Eugene, and Corvallis now each play a different role in the guide: city-base forays, compact weekends, event-driven travel, and academic meetups.
            </p>
          </article>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <article className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Featured stops</p>
            <p className="text-4xl font-serif text-accent mt-2">{TASTING_STOPS.length}</p>
          </article>
          <article className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Stay bases</p>
            <p className="text-4xl font-serif text-accent mt-2">{ACCOMMODATIONS.length}</p>
          </article>
          <article className="card-minimal p-6 bg-white/80">
            <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Route zones</p>
            <p className="text-4xl font-serif text-accent mt-2">{regions.length}</p>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-8 items-start">
          <article className="card-minimal p-8 bg-white/80 space-y-5">
            <span className="pill-minimal active">Winter guide</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">The season that already holds up under scrutiny.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Winter is still the easiest season to lead with because tours, festival programming, and spectator events are easier to verify in advance.</p>
              <p>That now applies across the Portland foothills leg, the Willamette Valley route, the Eugene event layer, and the smaller Corvallis academic leg.</p>
            </div>
          </article>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-5">
            <span className="pill-minimal">Spring White layer</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">The season that could make the guide special.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Spring White shoulder season still matters because it gives the guide a narrower second planning window beyond winter festival season.</p>
              <p>Official sources support that story through May in limited cases, especially for private adventures and training formats, but not as a broad summer-for-everyone claim. The guide is informational and is not representing or guaranteeing any third-party operator.</p>
            </div>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[1fr_0.95fr] gap-8 items-start">
          <article className="card-minimal p-8 bg-white/80 space-y-5">
            <span className="pill-minimal active">Why this state guide works</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Because the towns play different roles.</h2>
            <div className="space-y-4 text-lg text-ink-light leading-relaxed">
              <p>Portland, Estacada, Newberg, McMinnville, Dayton, and Eugene now support different parts of the Oregon guide: city pickups, foothills outings, stays, spectator events, training formats, and dining.</p>
              <p>That division makes it easier to expand the guide by zone instead of flattening the state into one interchangeable map.</p>
            </div>
          </article>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-4">
            <span className="pill-minimal">What this guide avoids</span>
            <div className="rounded-[24px] bg-white/80 border border-border p-5 text-ink-light leading-relaxed">
              It does not pretend every Oregon truffle mention belongs on the route.
            </div>
            <div className="rounded-[24px] bg-white/80 border border-border p-5 text-ink-light leading-relaxed">
              It does not flatten winter and spring into one interchangeable season.
            </div>
            <div className="rounded-[24px] bg-white/80 border border-border p-5 text-ink-light leading-relaxed">
              It does not recommend a stop just because it is nearby.
            </div>
          </article>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {regions.map((region) => (
            <article key={region.key} className="card-minimal p-6 bg-white/80 space-y-4">
              <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{region.shortTitle}</p>
              <h3 className="text-3xl font-serif tracking-tight">{region.title}</h3>
              <p className="text-sm text-ink-light leading-relaxed">{region.subtitle}</p>
              <p className="text-sm italic text-ink-light">{region.mood}</p>
              <div className="flex flex-wrap gap-2">
                {region.bestFor.slice(0, 4).map((item) => (
                  <span key={item} className="pill-minimal">{item}</span>
                ))}
              </div>
            </article>
          ))}
        </section>

        <section className="space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Featured stops</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Current route stops.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TASTING_STOPS.map((stop) => (
              <article key={stop.id} className="card-minimal p-6 bg-white/80 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stop.city}</p>
                  <h3 className="text-3xl font-serif tracking-tight">{stop.name}</h3>
                  <p className="text-sm text-ink-light leading-relaxed">{stop.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stop.highlights?.map((item) => (
                    <span key={item} className="pill-minimal">{item}</span>
                  ))}
                </div>
                <p className="text-sm text-ink-light leading-relaxed">
                  <span className="font-semibold text-ink">Season:</span> {stop.season}
                </p>
                {stop.routeNotes?.length ? (
                  <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                    {stop.routeNotes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                  </div>
                ) : null}
                {stop.verificationNote ? (
                  <div className="rounded-[18px] bg-sidebar/60 border border-border p-4 text-sm text-ink-light leading-relaxed">
                    <span className="font-semibold text-ink">Know before you go:</span> {stop.verificationNote}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">Places to stay</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Current lodging options in the guide.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ACCOMMODATIONS.map((stay) => (
              <article key={stay.id} className="card-minimal p-6 bg-white/80 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stay.city} / {stay.style}</p>
                  <h3 className="text-3xl font-serif tracking-tight">{stay.name}</h3>
                  <p className="text-sm text-ink-light leading-relaxed">{stay.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stay.amenities.map((item) => (
                    <span key={item} className="pill-minimal">{item}</span>
                  ))}
                </div>
                {stay.routeNotes?.length ? (
                  <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                    {stay.routeNotes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-3 pt-2">
                  <a
                    href={bookingSearchUrl(stay)}
                    target="_blank"
                    rel="noreferrer noopener sponsored"
                    data-stay22="book"
                    className="btn-minimal bg-accent text-white border-accent hover:bg-ink hover:border-ink"
                  >
                    Check rates
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

